import { IconSettings } from "@tabler/icons-react";
import { lazyNamed } from "../lib/lazyNamed";

/**
 * settingsRoute / profileRoute — genuinely shared across apps/portal,
 * apps/admin, and (profileRoute only) apps/auditor's still-in-frontend
 * surface. Moved here from frontend's app.portal.ts (frontend split epic
 * Phase 5, Task 5.2 follow-up) after a real-caller audit confirmed both are
 * imported by more than one portal's route table — the same audit found
 * notificationsRoute's own "shared across app, admin, and audit" comment was
 * stale: neither admin.portal.ts nor audit.portal.ts actually import it, so
 * it stayed in frontend, app-portal-only.
 *
 * frontend's app.portal.ts now imports these from @livingsyncs/ui instead
 * of defining them locally — same shim-repoint pattern Phase 2 already used
 * everywhere else.
 */

// Individually exported (not just used to assemble settingsRoute below) —
// apps/auditor's audit.portal.ts builds its OWN settings sub-route shape
// (no audit-logs tab, RouteMovedNotice instead of null for the two removed
// tabs), so it needs these page components directly rather than the
// assembled settingsRoute object.
export const SettingsPage = lazyNamed(
  () => import("../pages/settings/SettingsPage"),
  "SettingsPage",
);
export const SettingsIndexRedirect = lazyNamed(
  () => import("../pages/settings/SettingsIndexRedirect"),
  "SettingsIndexRedirect",
);
export const OrganizationSettingsPage = lazyNamed(
  () => import("../pages/settings/OrganizationSettingsPage"),
  "OrganizationSettingsPage",
);
export const FacilitySettingsPage = lazyNamed(
  () => import("../pages/settings/FacilitySettingsPage"),
  "FacilitySettingsPage",
);
const AuditLogsSettingsPage = lazyNamed(
  () => import("../pages/settings/AuditLogsSettingsPage"),
  "AuditLogsSettingsPage",
);
const ProfilePage = lazyNamed(
  () => import("../pages/ProfilePage"),
  "ProfilePage",
);

// Settings subtree (shared shape between the app and admin portals). `order: 99`
// keeps Settings last in both sidebars (it is the last nav item in each today).
export const settingsRoute = {
  path: "settings",
  Component: SettingsPage,
  nav: { label: "Settings", icon: IconSettings, order: 99 },
  children: [
    // Profile + Notifications moved to the standalone /profile page; the index
    // now lands on the first tab the user can see (Settings is manager/admin).
    { index: true, Component: SettingsIndexRedirect },
    { path: "organization", Component: OrganizationSettingsPage },
    { path: "facility", Component: FacilitySettingsPage },
    { path: "audit-logs", Component: AuditLogsSettingsPage },
    { path: "auditor-group", element: null },
    { path: "invitation-requests", element: null },
  ],
};

// Standalone personal account page (Profile + Notifications tabs), reached from
// each layout's header account menu rather than the sidebar. Shared across the
// app, admin, and audit portals so every user can reach their own profile.
export const profileRoute = { path: "profile", Component: ProfilePage };
